import type { ClipboardEvent,KeyboardEvent } from 'react';

import { validateGovernmentIdService } from '../../api/governmentId';
import { getInputTypeByCountry } from '../../helpers/getInputTypeByCountry';
import { FieldValidationProps, ValidationResultType } from '../../hooks/useFieldValidation';
import useSettings from '../../hooks/useSettings';
import { CountryCode } from '../../types/CountryCode';
import TextInputField from '../TextInputField/TextInputField';

type GovernmentIdFieldProps = {
    name: string;
    label?: string;
    countrycode: 'NO' | 'SE' | 'DK' | 'FI' | CountryCode;
};

const formatMap = new Map([
    ['NO', '^([0][1-9]|[1-2][0-9]|3[0-1])(0[1-9]|1[0-2])([0-9]{2})([\\d]{5})$'],
    ['SE', '^([0-9]{2})(0[1-9]|1[0-2])([0][1-9]|[1-2][0-9]|3[0-1])(-|\\+)?([\\d]{4})$'],
    ['DK', '^((3[0-1])|([1-2][0-9])|(0[1-9]))((1[0-2])|(0[1-9]))(\\d{2})(-)?([\\d]{4})$'],
    ['FI', '^([\\d]{6})[+-ABCDEFUVWXY]([\\d]{3})([0123456789ABCDEFHJKLMNPRSTUVWXY])$'],
]);

const formatErrorKeyMap = new Map([
    ['NO', 'validation.error.government.id.format.no'],
    ['SE', 'validation.error.government.id.format.se'],
    ['DK', 'validation.error.government.id.format.dk'],
    ['FI', 'validation.error.government.id.format.fi'],
]);

const GovernmentIdField = ({ countrycode, ...rest }: GovernmentIdFieldProps) => {
    const { proxyBaseUrl, apiKey } = useSettings();

    const governmentIdValidationService = (value?: string): Promise<ValidationResultType> =>
        validateGovernmentIdService(value, apiKey, proxyBaseUrl).then((valid) => ({
            valid,
            errorMessageKey: valid ? undefined : formatErrorKeyMap.get(countrycode),
        }));

    const validationProps = (): FieldValidationProps => ({
        format: formatMap.get(countrycode),
        formatErrorKey: formatErrorKeyMap.get(countrycode),
        customValidation: governmentIdValidationService,
    });

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (['NO', 'SE', 'DK'].includes(countrycode)) {
            if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                e.preventDefault();
            }
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData('text');
        if (['NO', 'SE', 'DK'].includes(countrycode)) {
            if (!/^\d+$/.test(pasted)) {
                e.preventDefault();
            }
        }
    };

    const inputType = getInputTypeByCountry(countrycode);

    return (
        <TextInputField
            {...rest}
            type={inputType}
            validation={validationProps()}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            inputProps={{
                inputMode: 'numeric',
                pattern: ['NO', 'SE', 'DK'].includes(countrycode) ? '[0-9]*' : undefined,
                style: {
                    MozAppearance: inputType === 'number' ? 'textfield' : undefined,
                },
            }}
            sx={{
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                },
                '& input[type=number]': {
                    MozAppearance: 'textfield',
                },
            }}
        />
    );
};

export default GovernmentIdField;
